import { FC, Fragment } from 'react';
import { Box, css, Typography } from '@mui/material';

import { getTheme } from 'src/utils/getTheme';

import { styles } from './styles';

import type { OptionType } from './types';

export const Select: FC<OptionType> = ({
    value,
    title,
    subtitle,
    onClick = () => {},
    isSelected = false,
    id,
    name,
}) => {
    const { theme, isHosTheme } = getTheme();
    const activeColor = isHosTheme ? theme.palette.secondary.dark : theme.palette.text.primary;
    const subtitleColorHos = isSelected ? theme.palette.common.white : theme.palette.secondary.dark;
    // Radio options render their text as spans: headings aren't valid inside a <label>, and every option
    // being a heading is noisy for screen readers
    const isRadio = Boolean(name);

    const content = (
        <Fragment>
            <Typography
                variant="h3"
                component={isRadio ? 'span' : 'h3'}
                color={isSelected ? theme.palette.common.white : theme.palette.text.primary}
                {...(isHosTheme && {
                    css: css({
                        fontWeight: 700,
                        color: isSelected ? theme.palette.common.white : activeColor,
                    }),
                })}
            >
                {title}
            </Typography>
            <Typography
                variant="h4"
                component={isRadio ? 'span' : 'h4'}
                color={isHosTheme ? subtitleColorHos : theme.palette.text.secondary}
                {...(isHosTheme && {
                    css: css({
                        fontSize: '15px',
                        color: isSelected ? theme.palette.common.white : activeColor,
                    }),
                })}
            >
                {subtitle}
            </Typography>
        </Fragment>
    );

    // With a name, render a native radio so the option is focusable, keyboard operable (arrow keys move
    // between options sharing the name) and announced with its checked state
    if (isRadio) {
        return (
            <Box
                component="label"
                css={[styles.container, styles.radioContainer]}
                bgcolor={isSelected ? activeColor : theme.palette.common.white}
                {...(id && { id })}
            >
                <input
                    type="radio"
                    css={styles.visuallyHidden}
                    name={name}
                    value={value}
                    checked={isSelected}
                    onChange={onClick}
                />
                {content}
            </Box>
        );
    }

    return (
        <Box
            css={styles.container}
            key={value}
            onClick={onClick}
            bgcolor={isSelected ? activeColor : theme.palette.common.white}
            {...(id && { id })}
        >
            {content}
        </Box>
    );
};
